import {
  Autocomplete,
  Box,
  Button,
  Fade,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRealEstateAddress,
  setRealEstateAddressOptions,
  setSelectedRealEstateAddress,
  setSelectedRealEstateAddressTemp,
} from "../../store/actions/uiActions/myPermitsActions";
import RenderCategoryBinds from "./realEstateComponents/RenderCategoryBinds";
import ConfirmSelectedAddress from "./realEstateComponents/ConfirmSelectedAddress";
import { setQuickActionAddress } from "../../store/actions/dataActions";
import { normalizeStreetName } from "../../utils/suffix";

const CustomPopper = (props) => (
  <Popper
    {...props}
    modifiers={[
      {
        name: "offset",
        options: {
          offset: [0, 4],
        },
      },
    ]}
    sx={{
      "& .MuiAutocomplete-paper": {
        marginTop: "1.5px",
        maxHeight: 220,
        width: "auto",
        maxWidth: "100%",
        overflowY: "auto", // Ensure only one scrollbar appears
      },
    }}
  />
);

const formatAddressOption = (option) => {
  if (!option || typeof option === "string") return option || "";
  const {
    address_number,
    prefix,
    street_name,
    street_type,
    unit,
    city,
    zip_code,
  } = option;

  const street = [prefix, street_name, street_type].filter(Boolean).join(" ");
  return `${address_number} ${street}${
    unit ? " #" + unit : ""
  }, ${city}, ${zip_code}`.trim();
};

const SpecialCreateNoc = () => {
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const { fetchLoading, presetLoading, addressOptions, selectedAddressTemp } =
    useSelector((state) => state.myPermitsComponent.realEstate);
  const { address: tempAddress } = useSelector(
    (state) => state.nocData.quickActions
  );

  const debounceTimerRef = useRef(null);
  const suppressFetchRef = useRef(false);
  const tempAddressRef = useRef(null);

  const realEstateSet = selectedRow?.realEstatePresets?.realEstateSet;
  const selectedPreset = selectedRow?.realEstatePresets?.selectedAddress;

  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [open, setOpen] = useState(false);
  const [enableButton, setEnableButton] = useState(realEstateSet === true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lastFetchedAddressRef = useRef(null);

  useEffect(() => {
    const handleFetchRealEstate = async () => {
      const normalizedAddress = normalizeStreetName(
        tempAddress?.fullStreetAddress
      );
      const options = await dispatch(
        fetchRealEstateAddress(normalizedAddress, true)
      ); // true = isQuickAction

      if (!Array.isArray(options) || options.length === 0) {
        await dispatch(
          setQuickActionAddress({
            fullStreetAddress: "",
            houseNumber: "",
            streetName: "",
          })
        );
        return;
      }

      const formattedInput = tempAddress.fullStreetAddress.toUpperCase();
      // Suppress the manual input fetch to prevent infinite loop
      suppressFetchRef.current = true;
      setInputValue(formattedInput);

      if (options.length === 1) {
        // Simulate user selecting it
        const option = options[0];
        setSelectedOption(option);
        dispatch(setSelectedRealEstateAddress(option));
        setEnableButton(true);
      } else {
        // Trigger dropdown to open
        setOpen(true);
      }

      // Clean up temp quickAction so it doesn't repeat
      await dispatch(
        setQuickActionAddress({
          fullStreetAddress: "",
          houseNumber: "",
          streetName: "",
        })
      );
    };

    if (tempAddress?.fullStreetAddress && !tempAddressRef?.current) {
      tempAddressRef.current = true;
      handleFetchRealEstate();
    }
  }, [tempAddress?.fullStreetAddress, dispatch]);

  useEffect(() => {
    if (!enableButton && selectedAddressTemp !== "") {
      setEnableButton(true);
    }
  }, [selectedAddressTemp, enableButton]);

  useEffect(() => {
    if (window.__suppressRealEstateFetch) {
      window.__suppressRealEstateFetch = false;
      return;
    }

    if (inputValue.trim() === lastFetchedAddressRef.current) return;
    lastFetchedAddressRef.current = inputValue.trim();

    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }

    if (!inputValue || typeof inputValue !== "string") return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      dispatch(fetchRealEstateAddress(inputValue.trim()));
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, dispatch]);

  const handleAddressChange = (e, value) => {
    if (selectedAddressTemp !== "") {
      dispatch(
        setSelectedRealEstateAddressTemp({
          fullAddress: "",
          shortAddress: "",
          selectedAddressTempObj: null,
        })
      );
    }
    suppressFetchRef.current = true;
    setSelectedOption(value);
    setInputValue(value ? formatAddressOption(value) : "");
    dispatch(setSelectedRealEstateAddress(value));

    if (value && !selectedRow?.realEstateSet) {
      setEnableButton(false);
      setTimeout(() => {
        setEnableButton(true);
      }, 1200); // 👈 delay for fade in
    }
  };

  return (
    <>
      <Box sx={{ px: 1, pt: 1 }}>
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 0, pb: 0 }}>
          {/* tart by entering the address of the property being improved */}
          Find and select the job address
        </Typography>
        <Typography
          fontWeight="bold"
          color="primary"
          variant="body2"
          sx={{
            m: 0,
            p: 0,
            lineHeight: "10px",
            marginTop: "0px !important",
            marginBottom: "0px !important",
          }}
        >
          (Can be partial or full)
        </Typography>
        <Autocomplete
          open={open}
          disabled={
            presetLoading || selectedRow?.realEstatePresets?.realEstateSet
          }
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          loading={fetchLoading}
          options={addressOptions}
          filterOptions={(options) => options} // Disable default filtering
          value={
            realEstateSet
              ? selectedPreset
              : selectedAddressTemp
                ? selectedAddressTemp
                : selectedOption
          }
          inputValue={
            inputValue !== ""
              ? inputValue
              : realEstateSet
                ? formatAddressOption(selectedPreset)
                : selectedAddressTemp
                  ? formatAddressOption(selectedAddressTemp)
                  : ""
          }
          getOptionLabel={formatAddressOption}
          noOptionsText={
            fetchLoading ? "Searching address..." : "No address found"
          }
          onInputChange={(event, newValue, reason) => {
            const cleaned = newValue.toUpperCase();
            setInputValue(cleaned);

            if (reason === "clear" || cleaned.trim() === "") {
              window.__suppressRealEstateFetch = false; // 💥 FIX: reset right away
              suppressFetchRef.current = true; // prevent fetch
              setSelectedOption(null);
              setInputValue("");
              dispatch(setSelectedRealEstateAddress(null));
              dispatch(setRealEstateAddressOptions([])); // 💣 wipe it
              dispatch(
                setSelectedRealEstateAddressTemp({
                  fullAddress: "",
                  shortAddress: "",
                  selectedAddressTempObj: null,
                })
              );
              setEnableButton(false);
            }
          }}
          onChange={handleAddressChange}
          renderOption={(props, option, { index }) => {
            const label = `${option.address_number} ${[
              option.prefix,
              option.street_name,
              option.street_type,
            ]
              .filter(Boolean)
              .join(" ")}${option.unit ? " #" + option.unit : ""}, ${
              option.city
            }, ${option.zip_code}`;

            return (
              <li {...props} key={`${option.parcel_number}-${index}`}>
                {label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Address"
              autoComplete="off"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  autoComplete: "new-password",
                },
              }}
              sx={{
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                MsUserSelect: "none",
              }}
            />
          )}
          slots={{ popper: CustomPopper }}
          sx={{ mt: 1 }}
          slotProps={{
            listbox: {
              style: {
                maxHeight: "200px", // Ensure the height is constrained
                overflowY: "auto", // Ensure scrolling
              },
            },
          }}
        />

        <Fade in={enableButton} timeout={{ enter: 600, exit: 200 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1,
              opacity: enableButton ? 1 : 0,
              transform: enableButton ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
            }}
          >
            <Button
              disabled={
                presetLoading || selectedRow?.realEstatePresets?.realEstateSet
              }
              size="small"
              sx={{ minWidth: "10rem" }}
              variant="contained"
              color="primary"
              onClick={() => setConfirmOpen(true)}
            >
              Select & Continue
            </Button>
          </Box>
        </Fade>

        {selectedRow?.realEstatePresets?.realEstateSet &&
          !selectedRow?.noc_created &&
          selectedRow?.connect_to_city?.is_connected && <RenderCategoryBinds />}
      </Box>

      <ConfirmSelectedAddress
        confirmOpen={confirmOpen}
        setConfirmOpen={setConfirmOpen}
        msg="Once you continue, you won’t be able to change the address."
        step={1}
      />
    </>
  );
};

export default SpecialCreateNoc;
